import { z } from "zod";
import { ApprovalRequirementSchema, AttachmentRefSchema, CareValidationCheckSchema, CodingSchema, DestinationRefSchema, EvidenceReferenceSchema, ExecutionPolicySchema, FollowUpTemplateSchema, FoundationActorRefSchema, FoundationAuditContextSchema, FoundationHashSchema, FoundationSemVerSchema, FoundationTimestampSchema, OwnerAssignmentSchema, PatientContextRefSchema, UncertaintySchema, } from "./foundation.js";
import { JsonValueSchema } from "./primitives.js";
export const CareBundleStatusSchema = z.enum([
    "draft",
    "assembling",
    "needs_information",
    "ready_for_review",
    "partially_approved",
    "approved",
    "executing",
    "completed",
    "cancelled",
    "superseded",
]);
export const CareActionLifecycleStatusSchema = z.enum([
    "proposed",
    "drafted",
    "validated",
    "needs_information",
    "ready_for_review",
    "approved",
    "rejected",
    "queued",
    "executing",
    "completed",
    "failed",
    "cancelled",
]);
export const CareActionPrioritySchema = z.enum(["routine", "soon", "urgent", "stat"]);
const careActionBaseShape = {
    actionId: z.string().min(1),
    lifecycleStatus: CareActionLifecycleStatusSchema,
    clinicalIntent: z.string().min(1),
    rationale: z.string().min(1),
    priority: CareActionPrioritySchema.optional(),
    evidenceRefs: z.array(EvidenceReferenceSchema),
    uncertainties: z.array(UncertaintySchema).optional(),
    validationResults: z.array(CareValidationCheckSchema),
    approvalRequirement: ApprovalRequirementSchema,
    executionPolicy: ExecutionPolicySchema,
    followUpTemplate: FollowUpTemplateSchema,
    dependencies: z.array(z.string()).optional(),
    supersedesActionId: z.string().optional(),
};
const ClinicalConceptSchema = z
    .object({
    display: z.string(),
    codings: z.array(CodingSchema).min(1).optional(),
})
    .strict();
export const CareReferralActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("referral"),
    payload: z
        .object({
        specialty: ClinicalConceptSchema,
        referralQuestion: z.string().min(1),
        clinicalSummary: z.string().optional(),
        urgencyRationale: z.string().optional(),
        letterDraft: z.string().min(1),
        destinationCandidates: z.array(DestinationRefSchema),
        selectedDestination: DestinationRefSchema.optional(),
        requirementsVersion: z.string().optional(),
        attachments: z.array(AttachmentRefSchema),
        missingPrerequisites: z.array(z.string()).optional(),
        patientPreferenceNotes: z.string().optional(),
    })
        .strict(),
})
    .strict();
export const CareMedicationActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("medication_request"),
    payload: z
        .object({
        medication: ClinicalConceptSchema.extend({
            form: z.string().optional(),
            strength: z.string().optional(),
        }).strict(),
        dose: z.string(),
        route: z.string(),
        frequency: z.string(),
        duration: z.string(),
        quantity: z.number().positive(),
        quantityUnit: z.string().optional(),
        repeats: z.number().int().min(0).optional(),
        indication: z.string().optional(),
        patientDirections: z.string(),
        pharmacy: DestinationRefSchema.optional(),
        startDate: z.string().date().optional(),
        endDate: z.string().date().optional(),
        substitutionAllowed: z.boolean().optional(),
        monitoringPlan: z.string().optional(),
    })
        .strict(),
})
    .strict();
export const CareLabOrderActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("lab_order"),
    payload: z
        .object({
        tests: z
            .array(z
            .object({
            display: z.string(),
            codings: z.array(CodingSchema).min(1).optional(),
            reason: z.string(),
            fasting: z.boolean().optional(),
            specimen: z.string().optional(),
            frequency: z.string().optional(),
        })
            .strict())
            .min(1),
        clinicalInformation: z.string(),
        diagnoses: z.array(CodingSchema).min(1).optional(),
        laboratory: DestinationRefSchema.optional(),
        requisitionTemplateId: z.string().optional(),
        requisitionTemplateVersion: z.string().optional(),
        copyTo: z.array(FoundationActorRefSchema).optional(),
    })
        .strict(),
})
    .strict();
export const CareImagingOrderActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("imaging_order"),
    payload: z
        .object({
        procedure: ClinicalConceptSchema,
        bodySite: z.string(),
        laterality: z
            .enum(["left", "right", "bilateral", "midline", "not_applicable", "unknown"])
            .optional(),
        clinicalQuestion: z.string(),
        clinicalInformation: z.string().optional(),
        diagnoses: z.array(CodingSchema).min(1).optional(),
        destination: DestinationRefSchema.optional(),
        contrast: z.string().optional(),
        pregnancyStatus: z.string().optional(),
        requisitionTemplateId: z.string().optional(),
        requisitionTemplateVersion: z.string().optional(),
    })
        .strict(),
})
    .strict();
export const CareCommunicationActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("communication"),
    payload: z
        .object({
        audience: z.array(FoundationActorRefSchema).min(1),
        channel: z.enum([
            "portal",
            "sms",
            "email",
            "phone_task",
            "print",
            "secure_message",
            "internal",
        ]),
        content: z.string().min(1),
        language: z.string().optional(),
        templateId: z.string().optional(),
        consentChecked: z.boolean().optional(),
        relatedActionIds: z.array(z.string()).optional(),
    })
        .strict(),
})
    .strict();
export const CareFollowUpActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("follow_up_task"),
    payload: z
        .object({
        reason: z.string(),
        ownerAssignment: OwnerAssignmentSchema,
        dueAt: FoundationTimestampSchema,
        taskType: z.string().optional(),
        instructions: z.string().optional(),
        escalationPolicyId: z.string().optional(),
        relatedActionIds: z.array(z.string()).optional(),
    })
        .strict(),
})
    .strict();
export const CareFormActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("form"),
    payload: z
        .object({
        formDefinitionId: z.string(),
        formVersion: z.string(),
        fieldValues: z.record(z.string(), JsonValueSchema),
        unansweredRequiredFields: z.array(z.string()).optional(),
        renderFormat: z.enum(["pdf", "html", "fhir_questionnaire_response", "other"]).optional(),
        destination: DestinationRefSchema.optional(),
    })
        .strict(),
})
    .strict();
export const CareGenericActionSchema = z
    .object({
    ...careActionBaseShape,
    actionType: z.literal("generic"),
    payload: z.record(z.string(), JsonValueSchema),
})
    .strict();
export const CareActionSchema = z.discriminatedUnion("actionType", [
    CareReferralActionSchema,
    CareMedicationActionSchema,
    CareLabOrderActionSchema,
    CareImagingOrderActionSchema,
    CareCommunicationActionSchema,
    CareFollowUpActionSchema,
    CareFormActionSchema,
    CareGenericActionSchema,
]);
export const CareBundleSchema = z
    .object({
    schemaVersion: FoundationSemVerSchema,
    bundleId: z.string(),
    bundleVersion: z.number().int().min(1),
    patientContext: PatientContextRefSchema,
    encounterId: z.string(),
    status: CareBundleStatusSchema,
    createdAt: FoundationTimestampSchema,
    createdBy: FoundationActorRefSchema,
    updatedAt: FoundationTimestampSchema.optional(),
    sourceSnapshot: z
        .object({
        snapshotId: z.string(),
        snapshotVersion: z.string(),
        contentHash: FoundationHashSchema,
        assembledAt: FoundationTimestampSchema,
        watermark: z.string().optional(),
        includedResourceVersions: z.record(z.string(), z.string()).optional(),
        stalenessSeconds: z.number().int().min(0).optional(),
    })
        .strict(),
    problems: z.array(z
        .object({
        problemId: z.string(),
        label: z.string(),
        codings: z.array(CodingSchema).min(1).optional(),
        certainty: z.enum(["confirmed", "working", "possible", "ruled_out", "historical"]),
        status: z.string().optional(),
        evidenceRefs: z.array(EvidenceReferenceSchema),
        notes: z.string().optional(),
    })
        .strict()),
    actions: z.array(CareActionSchema),
    overallRationale: z.string(),
    uncertainties: z.array(UncertaintySchema).optional(),
    validationSummary: z
        .object({
        status: z.enum(["not_run", "pass", "pass_with_warnings", "fail"]),
        blockingFailureCount: z.number().int().min(0),
        warningCount: z.number().int().min(0),
        lastValidatedAt: FoundationTimestampSchema.optional(),
        policyVersion: z.string().optional(),
    })
        .strict(),
    approvalPolicy: z
        .object({
        policyId: z.string(),
        policyVersion: z.string(),
        bundleApprovalAllowed: z.boolean(),
        requiresActionLevelDecisions: z.boolean().optional(),
        approvalOrder: z.array(z.string()).optional(),
        explanation: z.string().optional(),
    })
        .strict(),
    generationTrace: z
        .object({
        plannerId: z.string().optional(),
        plannerVersion: z.string().optional(),
        modelProvider: z.string().optional(),
        modelId: z.string().optional(),
        modelVersion: z.string().optional(),
        promptTemplateId: z.string().optional(),
        promptTemplateVersion: z.string().optional(),
        knowledgePackageVersions: z.array(z.string()).optional(),
        generatedAt: FoundationTimestampSchema.optional(),
        traceId: z.string().optional(),
    })
        .strict()
        .optional(),
    auditContext: FoundationAuditContextSchema.optional(),
})
    .strict();
//# sourceMappingURL=care-bundle.js.map